<script setup lang="ts">
import { localeTag } from '@/i18n'
// @ts-expect-error — layout composable is JS without type declarations
import { useLayout } from '@/layout/composables/layout'
import { ref, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useHelpStore } from '@/stores/help'
import { useChatbotStore } from '@/stores/chatbot'
import { useTourStore } from '@/stores/tour'
import { useGlobalSearchStore } from '@/stores/globalSearch'
import { useFavoritesStore } from '@/stores/favorites'
import { useScreenshotStore } from '@/stores/screenshot'
import { useSseNotifications, SSE_LABELS } from '@/composables/useSseNotifications'
import { useToast } from 'primevue/usetoast'
import PasspartoutSwitchDialog from '@/components/PasspartoutSwitchDialog.vue'
import FirmaSessioneIndicatore from '@/modules/firmaremota/components/FirmaSessioneIndicatore.vue'
import HelpDrawer from '@/modules/guida/components/HelpDrawer.vue'
import ChatbotPanel from '@/modules/chatbot/components/ChatbotPanel.vue'
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const { layoutState, toggleMenu, toggleConfigSidebar } = useLayout()
const globalSearch = useGlobalSearchStore()
const auth = useAuthStore()
const router = useRouter()
const currentRoute = useRoute()

/** Sdoppia la pagina corrente: stessa vista in entrambi i pannelli (default richiesto).
 *  Se siamo già nella vista affiancata, naviga e basta (mai /workspace dentro /workspace). */
function apriVistaAffiancata(): void {
  const path = currentRoute.fullPath
  if (currentRoute.path.startsWith('/workspace')) { void router.push('/workspace'); return }
  void router.push({ name: 'workspace', query: { left: path, right: path } })
}
const helpStore = useHelpStore()
const chatbotStore = useChatbotStore()
const tourStore = useTourStore()
const favoritesStore = useFavoritesStore()
const toast = useToast()

const screenshot = useScreenshotStore()

/**
 * Scatto dalla barra in alto: stessa azione della barra in basso, ma sempre a portata di clic
 * mentre si naviga (la barra in basso può restare fuori vista su pagine lunghe).
 */
async function scattaDaTopbar(): Promise<void> {
  if (screenshot.pieno || screenshot.inCorso) return
  try {
    await screenshot.scatta()
    toast.add({
      severity: 'success',
      summary: t('layout.topbar.schermataAcquisita', { n: screenshot.numeroScatti }, 'Schermata {n} acquisita'),
      detail: t('layout.topbar.tornaAlTicket', 'Torna al ticket dalla barra in basso per allegarla.'),
      life: 2500,
    })
  } catch {
    toast.add({ severity: 'error', summary: t('layout.topbar.schermataNonAcquisita', 'Schermata non acquisita'), life: 4000 })
  }
}

// Passpartout change-user — dialog a palette (PasspartoutSwitchDialog):
// ricerca col focus immediato, ultimo accesso, cambi recenti della postazione
const ppDialogVisible = ref(false)


const {
  enabled: notificheEnabled,
  notifications,
  unreadCount,
  latestNotification,
  toggle: toggleNotifiche,
  markAllRead,
  markRead,
  clearAll: clearNotifiche,
  isSupported: notificheSupported,
} = useSseNotifications()

// Toast automatico per ogni notifica in arrivo
watch(latestNotification, (n) => {
  if (!n) return
  toast.add({
    severity: 'info',
    summary: SSE_LABELS[n.type],
    detail: `${n.codice} — ${n.titolo}`,
    life: 6000,
  })
})

// Logo: usa il logo aziendale se disponibile, altrimenti il default Immigreer.
// Cache-buster per azienda: /api/v2/aziende/logo è un URL FISSO uguale per tutte le aziende,
// quindi dopo logout + login con un'altra azienda il browser riuserebbe il logo in cache.
// Appendendo ?v=<aziendaId> l'URL cambia al cambio azienda → il browser rifà la fetch.
const logoUrl = computed(() => {
  const url = auth.me?.logoUrl
  if (!url) return '/images/immigreer-logo.png'
  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}v=${auth.me?.aziendaId ?? ''}`
})
const logoError = ref(false)
// Resetta logoError quando cambia l'URL (cambio azienda / login)
watch(logoUrl, () => { logoError.value = false })

// Popover refs
const favoritesPanel = ref<{ show: (e: Event) => void; hide: () => void } | null>(null)
const notifPanel = ref<{ toggle: (e: Event) => void } | null>(null)

function openNotifiche(event: Event) {
  notifPanel.value?.toggle(event)
}

function onNotifClick(id: string, url: string) {
  markRead(id)
  if (url && url !== '/') router.push(url)
}

function formatNotifTime(d: Date): string {
  const now = new Date()
  const diff = Math.floor((now.getTime() - d.getTime()) / 1000)
  if (diff < 60) return t('layout.topbar.ora', 'ora')
  if (diff < 3600) return t('layout.topbar.minFa', { n: Math.floor(diff / 60) }, '{n} min fa')
  if (diff < 86400) return t('layout.topbar.oreFa', { n: Math.floor(diff / 3600) }, '{n} h fa')
  return new Intl.DateTimeFormat(localeTag(), { day: '2-digit', month: '2-digit' }).format(d)
}

function onTopbarMenuToggle() {
  layoutState.topbarMenuActive = !layoutState.topbarMenuActive
}

function toggleFavorites(event: Event) {
  favoritesPanel.value?.show(event)
}

function toggleCurrentPage() {
  const path = currentRoute.path
  const label = (currentRoute.meta?.title as string | undefined) ?? String(currentRoute.name ?? path)
  if (favoritesStore.isFavorite(path)) {
    favoritesStore.remove(path)
  } else {
    favoritesStore.add({ path, label })
  }
}
</script>

<template>
  <div class="layout-topbar">
    <div class="layout-topbar-start">
      <router-link
        class="layout-topbar-logo"
        to="/"
      >
        <img
          v-if="!logoError"
          :src="logoUrl"
          alt="Immigreer"
          class="layout-topbar-logo-full"
          @error="logoError = true"
        >
        <span
          v-else
          class="layout-topbar-logo-full font-bold text-xl"
        >Immigreer</span>
        <span class="layout-topbar-logo-slim font-bold text-xl">I</span>
      </router-link>

      <a
        class="layout-menu-button"
        @click="toggleMenu"
      >
        <i class="pi pi-chevron-right" />
      </a>

      <button
        class="app-config-button app-config-mobile-button"
        type="button"
        @click="toggleConfigSidebar"
      >
        <i class="pi pi-cog" />
      </button>

      <a
        class="layout-topbar-mobile-button"
        @click="onTopbarMenuToggle"
      >
        <i class="pi pi-ellipsis-v" />
      </a>
    </div>

    <div class="layout-topbar-end">
      <div class="layout-topbar-actions-start">
        <!-- placeholder per MegaMenu futuro -->
      </div>
      <div class="layout-topbar-actions-end">
        <ul class="layout-topbar-items">
          <!-- Modalità cattura schermate: compare solo quando è attiva, accanto al
               passpartout, così è raggiungibile da qualunque pagina mentre si naviga. -->
          <li v-if="screenshot.attivo">
            <a
              class="cursor-pointer"
              :title="screenshot.pieno
                ? t('layout.topbar.limiteSchermate', 'Limite schermate raggiunto')
                : t('layout.topbar.scattaSchermata', { n: screenshot.numeroScatti }, 'Scatta schermata di questa pagina ({n} finora)')"
              :class="{ 'opacity-50': screenshot.pieno || screenshot.inCorso }"
              @click="scattaDaTopbar"
            >
              <i class="pi pi-camera" />
              <span
                v-if="screenshot.numeroScatti"
                class="screenshot-count"
              >{{ screenshot.numeroScatti }}</span>
            </a>
          </li>

          <!-- Sessione di firma digitale aperta: conto alla rovescia + «Chiudi sessione» -->
          <FirmaSessioneIndicatore />

          <!-- Passpartout: cambia utente (bottone + Popover) -->
          <li v-if="auth.me?.passpartout">
            <a
              class="cursor-pointer pp-key-btn"
              :title="t('layout.topbar.passpartout', 'Sessione passpartout — cambia utente')"
              @click="ppDialogVisible = true"
            >
              <i class="pi pi-key" />
            </a>
          </li>

          <!-- Ricerca -->
          <li class="layout-topbar-search">
            <a
              id="btn-global-search"
              :title="t('layout.topbar.cerca', 'Cerca (Ctrl/⌘ + K)')"
              @click="globalSearch.open()"
            >
              <i class="pi pi-search" />
            </a>
          </li>

          <!-- Vista affiancata -->
          <li>
            <a
              :title="t('workspace.apri', 'Vista affiancata')"
              @click="apriVistaAffiancata"
            >
              <i class="pi pi-clone" />
            </a>
          </li>

          <!-- Config tema -->
          <li>
            <button
              class="app-config-button"
              type="button"
              @click="toggleConfigSidebar"
            >
              <i class="pi pi-cog" />
            </button>
          </li>

          <!-- Preferiti -->
          <li>
            <a
              class="cursor-pointer"
              :title="t('layout.topbar.preferiti', 'Preferiti')"
              @click="toggleFavorites($event)"
            >
              <i
                :class="favoritesStore.isFavorite(currentRoute.path) ? 'pi pi-star-fill' : 'pi pi-star'"
                :style="favoritesStore.isFavorite(currentRoute.path) ? 'color: var(--p-yellow-400)' : ''"
              />
            </a>
          </li>

          <!-- Tour pagina (visibile solo se la route ha hasTour) -->
          <li v-if="currentRoute.meta.hasTour">
            <a
              class="cursor-pointer"
              :title="t('layout.topbar.avviaTour', 'Avvia tour guidato')"
              @click="tourStore.start()"
            >
              <i class="pi pi-play" />
            </a>
          </li>

          <!-- Notifiche SSE: campanella con badge unread -->
          <li
            v-if="notificheSupported()"
            class="notif-bell-item"
          >
            <a
              class="cursor-pointer notif-bell-btn"
              :title="t('layout.topbar.notifiche', 'Notifiche')"
              :aria-label="t('layout.topbar.apriNotifiche', 'Apri notifiche')"
              @click="openNotifiche($event)"
            >
              <i :class="notificheEnabled ? 'pi pi-bell' : 'pi pi-bell-slash'" />
              <Badge
                v-if="unreadCount > 0"
                :value="unreadCount > 9 ? '9+' : String(unreadCount)"
                severity="danger"
                class="notif-badge"
              />
            </a>
          </li>

          <!-- Help contestuale -->
          <li v-if="currentRoute.meta.helpSlug">
            <a
              id="btn-help"
              class="cursor-pointer"
              :title="t('layout.topbar.guidaPagina', 'Guida pagina')"
              @click="helpStore.open(currentRoute.meta.helpSlug!)"
            >
              <i class="pi pi-question-circle" />
            </a>
          </li>

          <!-- Help senza helpSlug (fallback per pagine senza guida ma con tour) -->
          <li v-else-if="currentRoute.meta.hasTour">
            <a
              id="btn-help"
              class="cursor-pointer"
              :title="t('layout.topbar.guidaPagina', 'Guida pagina')"
            >
              <i class="pi pi-question-circle" />
            </a>
          </li>

          <!-- Chatbot AI (solo se CHATBOT_USE) -->
          <li v-if="auth.has('CHATBOT_USE')">
            <a
              id="btn-chatbot-ai"
              class="cursor-pointer"
              :title="t('layout.topbar.assistenteAi', 'Assistente AI')"
              @click="chatbotStore.toggleOpen()"
            >
              <i class="pi pi-comments" />
            </a>
          </li>

          <!-- (Area utente rimossa: profilo/lingua/cambia password/logout sono ora nel menu
               profilo in basso a sinistra, AppMenuProfile.vue — niente più doppione qui.) -->

          <!-- Pannello destro: Operatori in servizio (solo con permesso Personale) -->
          <li v-if="auth.has('PERSONALE_READ')">
            <a
              v-tooltip.bottom="t('layout.topbar.operatoriInServizio', 'Operatori in servizio')"
              @click="layoutState.rightMenuActive = true"
            >
              <i class="pi pi-users" />
            </a>
          </li>
        </ul>
      </div>
    </div>
  </div>

  <!-- Dialog passpartout cambia utente (palette con ultimo accesso e cambi recenti) -->
  <PasspartoutSwitchDialog v-model:visible="ppDialogVisible" />

  <!-- Popover Preferiti -->
  <Popover ref="favoritesPanel">
    <div class="favorites-panel">
      <div class="favorites-header">
        <span class="favorites-title">{{ t('layout.topbar.preferiti', 'Preferiti') }}</span>
        <Button
          :icon="favoritesStore.isFavorite(currentRoute.path) ? 'pi pi-star-fill' : 'pi pi-star'"
          :label="favoritesStore.isFavorite(currentRoute.path) ? t('layout.topbar.rimuovi', 'Rimuovi') : t('layout.topbar.aggiungiPagina', 'Aggiungi pagina')"
          text
          size="small"
          @click="toggleCurrentPage"
        />
      </div>
      <div
        v-if="favoritesStore.items.length === 0"
        class="favorites-empty"
      >
        {{ t('layout.topbar.nessunPreferito', 'Nessun preferito ancora') }}
      </div>
      <ul
        v-else
        class="favorites-list"
      >
        <li
          v-for="fav in favoritesStore.items"
          :key="fav.path"
          class="favorites-item"
        >
          <router-link
            :to="fav.path"
            class="fav-link"
            @click="favoritesPanel?.hide()"
          >
            <i class="pi pi-link" />
            {{ fav.label }}
          </router-link>
          <Button
            icon="pi pi-times"
            text
            rounded
            size="small"
            severity="secondary"
            @click="favoritesStore.remove(fav.path)"
          />
        </li>
      </ul>
    </div>
  </Popover>

  <!-- Popover notifiche (fuori dal ul per non rompere la struttura lista) -->
  <Popover
    ref="notifPanel"
    class="notif-overlay"
  >
    <div class="notif-panel">
      <div class="notif-panel-head">
        <span class="notif-panel-title">{{ t('layout.topbar.notifiche', 'Notifiche') }}</span>
        <div class="notif-panel-actions">
          <Button
            v-if="notifications.length > 0"
            :label="t('layout.topbar.segnaLette', 'Segna tutte lette')"
            text
            size="small"
            @click="markAllRead()"
          />
          <Button
            v-if="notifications.length > 0"
            icon="pi pi-trash"
            text
            rounded
            severity="secondary"
            size="small"
            :title="t('layout.topbar.cancellaTutto', 'Cancella tutto')"
            @click="clearNotifiche()"
          />
          <Button
            :icon="notificheEnabled ? 'pi pi-bell' : 'pi pi-bell-slash'"
            :severity="notificheEnabled ? 'secondary' : 'info'"
            text
            rounded
            size="small"
            :title="notificheEnabled ? t('layout.topbar.disattivaSse', 'Disattiva notifiche SSE') : t('layout.topbar.attivaSse', 'Attiva notifiche SSE')"
            @click="toggleNotifiche()"
          />
        </div>
      </div>

      <div
        v-if="notifications.length === 0"
        class="notif-empty"
      >
        <i class="pi pi-bell-slash notif-empty-icon" />
        <p>{{ t('layout.topbar.nessunaNotifica', 'Nessuna notifica') }}</p>
        <p
          v-if="!notificheEnabled"
          class="notif-empty-hint"
        >
          {{ t('layout.topbar.notificheDisattivate', 'Notifiche disattivate') }} —
          <Button
            :label="t('layout.topbar.attiva', 'Attiva')"
            text
            size="small"
            class="p-0"
            @click="toggleNotifiche()"
          />
        </p>
      </div>

      <div
        v-else
        class="notif-list"
      >
        <div
          v-for="n in notifications"
          :key="n.id"
          :class="['notif-item', { 'notif-item--unread': !n.read }]"
          @click="onNotifClick(n.id, n.url)"
        >
          <div class="notif-item-top">
            <span class="notif-item-label">{{ SSE_LABELS[n.type] }}</span>
            <span class="notif-item-time">{{ formatNotifTime(n.timestamp) }}</span>
          </div>
          <div class="notif-item-body">
            {{ n.codice }} — {{ n.titolo }}
          </div>
        </div>
      </div>
    </div>
  </Popover>

  <HelpDrawer />
  <ChatbotPanel />
</template>

<style scoped>
/* ── Logo leggibile su qualunque tema di topbar ──────────────────────────────
   Il logo è UNA sola immagine a colori con lettering scuro, e la topbar ha 20 temi:
   19 hanno la fascia del logo (`--topbar-start-bg`) scura o satura — su indigo
   (#283593) il logo diventava illeggibile. Vale anche, e soprattutto, per i loghi
   caricati dalle aziende: sono i loro file, non possiamo sceglierne i colori.
   Quindi il logo si appoggia su una piastrina chiara, come il badge del brand già in
   uso su login e landing page: qualunque immagine resta leggibile su qualunque tema.
   Esclusa la sola topbar bianca (tema di default), dove la piastrina non servirebbe. */
/* Le misure stavano in uno `style` inline sull'immagine: da qui si possono adattare al
   contesto, cosa che l'attributo non permetteva (vince sempre sul foglio di stile). */
.layout-topbar-logo-full {
  max-height: 50px;
  max-width: 200px;
  object-fit: contain;
}

.layout-wrapper:not(.layout-topbar-white) .layout-topbar-logo {
  padding: 0.3rem 0.55rem;
  /* Il pulsante del menu sta a `right: -1.25rem` della fascia, cioè le sporge addosso:
     senza questo margine la piastrina gli finiva sotto. */
  margin-right: 1.1rem;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.22);
}

/* Con la piastrina il logo si rimpicciolisce: a 50px il chip riempiva i 4rem della topbar e
   pareva incollato ai bordi, invece di esserci appoggiato. */
.layout-wrapper:not(.layout-topbar-white) .layout-topbar-logo-full {
  max-height: 38px;
  /* I loghi larghi (per esempio quelli con la denominazione accanto al simbolo) usavano
     tutti i 200px e la piastrina toccava il pulsante: 175 lasciano lo spazio. */
  max-width: 175px;
}

/* In modalità slim resta la sola iniziale, che prende il colore del testo del tema:
   la piastrina lì sarebbe un quadratino bianco attorno a una lettera. */
.layout-wrapper.layout-slim:not(.layout-topbar-white) .layout-topbar-logo,
.layout-wrapper.layout-slim-plus:not(.layout-topbar-white) .layout-topbar-logo {
  padding: 0;
  background: none;
  box-shadow: none;
}

/* Contatore schermate sull'icona della fotocamera in topbar */
.screenshot-count {
  position: absolute;
  transform: translate(0.6rem, -0.7rem);
  min-width: 1rem;
  height: 1rem;
  padding: 0 0.2rem;
  border-radius: 999px;
  background: var(--p-primary-color);
  color: var(--p-primary-contrast-color);
  font-size: 0.65rem;
  line-height: 1rem;
  text-align: center;
  font-weight: 700;
}

/* ── Menu utente (Area utente) ── */
.user-menu {
  min-width: 230px;
}
.user-menu-header {
  padding: 0.625rem 1rem;
  font-weight: 600;
  font-size: 0.875rem;
  border-bottom: 1px solid var(--p-surface-200);
}
.user-menu-label {
  padding: 0.5rem 1rem 0.25rem;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--p-text-muted-color);
}
.user-menu-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.5rem 1rem;
  transition: background 0.15s;
}
.user-menu-item:hover {
  background: var(--p-surface-100);
}
.user-menu-icon {
  width: 1rem;
  color: var(--p-text-muted-color);
}
.user-menu-check {
  margin-left: auto;
  color: var(--p-primary-color);
  font-size: 0.8rem;
}
.user-menu-sep {
  height: 1px;
  background: var(--p-surface-200);
  margin: 0.375rem 0;
}

/* ── Passpartout ── */
.pp-key-btn {
  display: flex;
  align-items: center;
  justify-content: center;
}
/* Badge ad alto contrasto: la chiave era violetto su topbar indigo (tono su tono,
   quasi invisibile). Ambra + icona scura si vede su topbar scure E chiare, e segnala
   a colpo d'occhio che la sessione passpartout è attiva. */
.pp-key-btn .pi-key {
  background: #fbbf24;            /* amber-400 */
  color: #1f2937;                 /* slate-800 */
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.4);
}
.pp-key-loading {
  opacity: 0.5;
  pointer-events: none;
}
.pp-popover {
  --p-popover-min-width: 280px;
}
.pp-popover-inner {
  padding: 0.25rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.pp-popover-title {
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0 0 0.25rem;
  border-bottom: 1px solid var(--p-surface-200);
  display: flex;
  align-items: center;
}
.pp-ac-full :deep(.p-autocomplete-input) {
  width: 100%;
}
.pp-hint {
  font-size: 0.75rem;
  color: var(--p-text-muted-color);
  margin: 0;
}
.pp-opt {
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}
.pp-opt-name {
  font-weight: 600;
  font-size: 0.85rem;
}
.pp-opt-username {
  font-weight: 400;
  font-size: 0.78rem;
  color: var(--p-text-muted-color);
}
.pp-opt-ruoli {
  font-style: italic;
}
.pp-opt-az {
  font-size: 0.75rem;
  color: var(--p-text-muted-color);
}

.favorites-panel {
  min-width: 240px;
  max-width: 320px;
  padding: 0.25rem 0;
}

.favorites-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem 0.375rem;
  border-bottom: 1px solid var(--p-surface-200);
  margin-bottom: 0.375rem;
}

.favorites-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--p-text-color);
}

.favorites-empty {
  padding: 0.75rem;
  font-size: 0.8125rem;
  color: var(--p-text-muted-color);
  text-align: center;
}

.favorites-list {
  list-style: none;
  margin: 0;
  padding: 0.25rem 0;
}

.favorites-item {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0 0.5rem;
}

.fav-link {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.25rem;
  font-size: 0.875rem;
  color: var(--p-text-color);
  text-decoration: none;
  border-radius: 0.25rem;
  transition: background 0.12s;
  min-width: 0;
}

.fav-link:hover {
  background: var(--p-surface-100);
  color: var(--p-primary-color);
}

.fav-link i {
  font-size: 0.8125rem;
  color: var(--p-text-muted-color);
  flex-shrink: 0;
}

.fav-link span,
.fav-link {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Campanella notifiche ── */
.notif-bell-item {
  position: relative;
}

.notif-bell-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.notif-badge {
  position: absolute;
  top: -6px;
  right: -8px;
  font-size: 0.6rem !important;
  min-width: 16px !important;
  height: 16px !important;
  line-height: 16px !important;
  padding: 0 3px !important;
}

/* ── Overlay panel notifiche ── */
.notif-panel {
  width: 340px;
  max-width: 92vw;
}

.notif-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.25rem 0.5rem 0;
  border-bottom: 1px solid var(--p-surface-200);
  margin-bottom: 0.5rem;
  gap: 0.5rem;
}

.notif-panel-title {
  font-weight: 700;
  font-size: 0.95rem;
  color: var(--p-text-color);
}

.notif-panel-actions {
  display: flex;
  align-items: center;
  gap: 0.125rem;
}

.notif-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem 1rem;
  gap: 0.5rem;
  color: var(--p-text-muted-color);
  text-align: center;
}

.notif-empty-icon {
  font-size: 2rem;
  color: var(--p-surface-400);
}

.notif-empty p {
  margin: 0;
  font-size: 0.875rem;
}

.notif-empty-hint {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.8rem !important;
}

.notif-list {
  max-height: 340px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.notif-item {
  padding: 0.5rem 0.625rem;
  border-radius: var(--p-border-radius-md, 6px);
  cursor: pointer;
  transition: background 0.15s;
  border-left: 3px solid transparent;
}

.notif-item:hover {
  background: var(--p-surface-100);
}

.notif-item--unread {
  border-left-color: var(--p-primary-color);
  background: color-mix(in srgb, var(--p-primary-color) 5%, transparent);
}

.notif-item--unread:hover {
  background: color-mix(in srgb, var(--p-primary-color) 10%, transparent);
}

.notif-item-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.15rem;
}

.notif-item-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--p-primary-color);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.notif-item-time {
  font-size: 0.7rem;
  color: var(--p-text-muted-color);
}

.notif-item-body {
  font-size: 0.8125rem;
  color: var(--p-text-color);
  line-height: 1.3;
}
</style>
